import {useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {
    fetchClubIDFromDatabase, fetchEventByIdFromDatabase,
    fetchParticipantsByClubIDFromDatabase,
    getAmountOfParticipantsInBoat,
} from "../handlers/databasehandler";
import BodyContainer from "../components/BodyContainer";
import MessageBox from "../components/MessageBar";
import ProgressRing from "../components/ProgressRing";
import Title from "../components/Title";

import {Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button,} from "@fluentui/react-components";
import ParticipantsObject from "../components/ParticipantsObject";
import Distance from "../models/Distance";
import {getDistanceData, getParticipantsByDistanceId} from "../handlers/registrationhandler";
import AddParticipantButton from "../components/AddParticipantButton";
import {Add12Filled, ArrowDownload16Regular} from "@fluentui/react-icons";
import {exportRegistrationsPDF} from "../handlers/pdfhandler";

const RegisterView = () => {
    const { id } = useParams();

    const [participantCounts, setParticipantCounts] = useState({});
    const [errorMsg, setErrorMsg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [distances, setDistances] = useState([]);
    const [distanceRows, setDistanceRows] = useState([]);
    const [clubParticipants, setParticipants] = useState([]);
    const [clubId, setClubId] = useState(null);
    const [eventName, setEventName] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                let eventData = await fetchEventByIdFromDatabase(id);
                if (!eventData) {
                    setErrorMsg("Fout bij laden evenement data");
                    setLoading(false);
                    return;
                }

                if (eventData.registration_end && new Date(eventData.registration_end) < new Date()) {
                    setErrorMsg(`Evenement is gesloten voor inschrijving. ${eventData.contact_email ? `Na-inschrijvingen of andere wijzigingen kunnen worden doorgegeven via: ${eventData.contact_email}` : ``}`);
                    setLoading(false);
                    return;
                }

                eventData.name = eventData.name ?? id;
                setEventName(eventData.name)

                const data = await getDistanceData(id);

                if (!data) {
                    setErrorMsg("Fout bij laden afstandsdata");
                    setLoading(false);
                    return;
                }

                setDistances(data.map(d => new Distance(d.id, d.distance, d.Gender, d.Categories, d.Boat)));

                const clubIdResult = await fetchClubIDFromDatabase();
                if (!clubIdResult) {
                    setErrorMsg("Fout bij ophalen club gegevens");
                    setLoading(false);
                    return;
                }

                setClubId(clubIdResult.id);

                const map = [];
                for (const distance of data) {
                    const participants = await getParticipantsByDistanceId(distance.id, clubIdResult.id);

                    if (participants != null) {
                        map[distance.id] = participants;

                    } else {
                        setErrorMsg("Fout bij laden afstandsdata");
                        setLoading(false);
                        return;
                    }
                }
                setDistanceRows(map);

                const counts = {};
                for (const distanceId in map) {
                    for (const row of map[distanceId]) {
                        counts[row.id] = await getAmountOfParticipantsInBoat(row.id, distanceId);
                    }
                }
                setParticipantCounts(counts);

                const clubParticipants = await fetchParticipantsByClubIDFromDatabase(clubIdResult.id);
                setParticipants(clubParticipants);

            } catch (error) {
                console.error(error);
                setErrorMsg("An error occurred while loading data.");
            }

            setLoading(false);
        };

        fetchData().then(r => {});
    }, [id]);

    const refreshParticipants = async (distanceId, rowId) => {
        if (!clubId) return;

        if (rowId != null) {
            const count = await getAmountOfParticipantsInBoat(rowId, distanceId);
            setParticipantCounts(prev => ({
                ...prev,
                [rowId]: count
            }));
        } else {
            const counts = {};
            for (const distanceId in distanceRows) {
                for (const row of distanceRows[distanceId]) {
                    counts[row.id] = await getAmountOfParticipantsInBoat(row.id, distanceId);
                }
            }
            setParticipantCounts(counts);
        }

        const participants = await getParticipantsByDistanceId(distanceId, clubId);
        if (participants != null) {
            setDistanceRows(prev => ({
                ...prev,
                [distanceId]: participants
            }));
        }
    };

    const handleUpdateParticipants = (newParticipant) => {
        setParticipants([...clubParticipants, newParticipant]);
    };


    const genders = Array.from(new Set(distances.map(d => d.Gender?.gender)));

    return (
        <BodyContainer
            showBackButton={true}
            content={
                <>
                    <Title title="Inschrijven" />
                    {genders.length > 0 &&
                        <div className={`flex`}>
                            <Button as={`a`} className={`button`} appearance={`primary`} onClick={async () => exportRegistrationsPDF(eventName, distances, distanceRows)} disabled={loading}>
                                <ArrowDownload16Regular/> Download
                            </Button>
                        </div>
                    }

                    {errorMsg && (
                        <MessageBox
                            intent={"error"}
                            title={"Er is een fout opgetreden: "}
                            message={errorMsg}
                            closeButtonFunction={() => setErrorMsg(null)}
                        />
                    )}

                    {loading &&
                        <div className={`flex flex-gap-medium flex-justify-center`}>
                            <ProgressRing />
                            <p>Inschrijvingen worden geladen, dit kan even duren</p>
                        </div>
                    }

                    {!loading && !errorMsg && (
                        <Accordion collapsible multiple>
                            {genders.map((gender, idx) => {
                                const genderDistances = distances.filter(d => d.Gender?.gender === gender);
                                const categories = Array.from(
                                    new Set(genderDistances.map(d => d.Categories?.category))
                                );

                                return (
                                    <AccordionItem key={idx} value={gender}>
                                        <AccordionHeader>{gender}</AccordionHeader>
                                        <AccordionPanel>
                                            <Accordion collapsible multiple>
                                                {categories.map((category, cidx) => {
                                                    const categoryDistances = genderDistances.filter(
                                                        d => d.Categories?.category === category
                                                    );
                                                    const uniqueDistances = Array.from(
                                                        new Set(categoryDistances.map(d => d.distance))
                                                    );

                                                    return (
                                                        <AccordionItem key={cidx} value={`${gender}-${category}`}>
                                                            <AccordionHeader>{category}</AccordionHeader>
                                                            <AccordionPanel>
                                                                <Accordion collapsible multiple>
                                                                    {uniqueDistances.map((distance, didx) => {
                                                                        const distanceItems = categoryDistances.filter(
                                                                            d => d.distance === distance
                                                                        );

                                                                        return (
                                                                            <AccordionItem key={didx} value={`${gender}-${category}-${distance}`}>
                                                                                <AccordionHeader>{distance}</AccordionHeader>
                                                                                <AccordionPanel>
                                                                                    <Accordion collapsible multiple>
                                                                                        {distanceItems.map((item, i) => {
                                                                                            const distanceId = item.id;
                                                                                            const boat = item.Boat?.boat || "Unknown Boat";
                                                                                            const amountOfParticipantsInBoat = item.Boat?.amount_of_people || 1;
                                                                                            const participants = [];

                                                                                            for (const distanceRow of distanceRows[distanceId]) {
                                                                                                for (let i = 1; i <= amountOfParticipantsInBoat; i++) {
                                                                                                    const key = i === 1 ? "participant_id" : `participant_id${i}`;
                                                                                                    const participant = distanceRow[key];
                                                                                                    if (participant != null) {
                                                                                                        participants.push({
                                                                                                            participant: distanceRow[key],
                                                                                                            id: distanceRow.id,
                                                                                                            key: key,
                                                                                                        });
                                                                                                    }
                                                                                                }
                                                                                            }

                                                                                            return (
                                                                                                <AccordionItem
                                                                                                    key={`${distanceId}-${i}`}
                                                                                                    value={`${gender}-${category}-${distance}-${boat}`}
                                                                                                >
                                                                                                    <AccordionHeader>{boat}</AccordionHeader>
                                                                                                    <AccordionPanel>
                                                                                                        {participants ? (
                                                                                                            participants.length > 0 ? (
                                                                                                                <div>
                                                                                                                    {participants.map((p) => (
                                                                                                                        <ParticipantsObject key={`${distanceId}-${p.id}-${p.key}-${p.participant.id}`}
                                                                                                                                            distanceId={distanceId}
                                                                                                                                            name={p.participant.first_name + " " + p.participant.last_name}
                                                                                                                                            registrationId={p.id} participantsId={p.participant.id}
                                                                                                                                            participantKey={(item.Boat?.amount_of_people > 1) ? p.key : null}
                                                                                                                                            onDeleted={() => refreshParticipants(distanceId, p.id)}
                                                                                                                                            amountOfParticipantsInBoat={participantCounts[p.id] || 1}/>
                                                                                                                    ))}
                                                                                                                </div>
                                                                                                            ) : (
                                                                                                                <p>Geen deelnemers gevonden</p>
                                                                                                            )
                                                                                                        ) : (
                                                                                                            <p>Geen deelnemers gevonden</p>
                                                                                                        )}
                                                                                                        <AddParticipantButton
                                                                                                                            key={`${distanceId}-${i}-${participants?.length ?? 0}`}
                                                                                                                            text={<><Add12Filled/>  Deelnemer toevoegen</>}
                                                                                                                            distanceId={distanceId}
                                                                                                                            eventId={id}
                                                                                                                            participants={clubParticipants}
                                                                                                                            clubId={clubId}
                                                                                                                            maxParticipantsInBoat={amountOfParticipantsInBoat}
                                                                                                                            onAdded={() => refreshParticipants(distanceId , null)}
                                                                                                                            onUpdateParticipants={() => handleUpdateParticipants}
                                                                                                        />
                                                                                                    </AccordionPanel>
                                                                                                </AccordionItem>
                                                                                            );
                                                                                        })}
                                                                                    </Accordion>
                                                                                </AccordionPanel>
                                                                            </AccordionItem>
                                                                        );
                                                                    })}
                                                                </Accordion>
                                                            </AccordionPanel>
                                                        </AccordionItem>
                                                    );
                                                })}
                                            </Accordion>
                                        </AccordionPanel>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>

                    )}
                </>
            }
        />
    );
};

export default RegisterView;
